// Matches the mobile cover_image_logo_helpers crop, alpha and contrast rules.
const LOGO_ALPHA_THRESHOLD = 0.08;
// A light surface above this gets black ink; logo ink above it disappears on white.
export const LOGO_CONTRAST_THRESHOLD = 0.6;
const OPAQUE_COVERAGE_THRESHOLD = 0.95;
const LOW_ALPHA_THRESHOLD = 0.7;
const CONTRAST_PERCENTILE = 0.25;
const CARD_OVERLAY_MULTIPLIER = 0.8;
// A wordmark's ink stays in a tight luminance band. A full-color tile does not.
const MONOCHROME_LUMINANCE_VARIANCE = 0.04;

export const DISCOUNT_CODE_CARD_IMAGE_SCALE = 1.3;
export const COVER_IMAGE_LOGO_WIDTH = 128;
export const COVER_IMAGE_LOGO_HEIGHT = 52;

export const getCoverImageLogoContentBounds = alphaGrid => {
  const { alphaValues, columns, rows, sourceWidth, sourceHeight } = alphaGrid || {};
  if (!alphaValues?.length || !columns || !rows || !sourceWidth || !sourceHeight) return null;

  let minX = columns;
  let minY = rows;
  let maxX = -1;
  let maxY = -1;
  let visibleCount = 0;
  alphaValues.forEach((alpha, index) => {
    if (alpha <= LOGO_ALPHA_THRESHOLD) return;
    const x = index % columns;
    const y = Math.floor(index / columns);
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
    visibleCount++;
  });

  if (!visibleCount) {
    return {
      x: 0,
      y: 0,
      width: 1,
      height: 1,
      sourceWidth,
      sourceHeight,
      contentAlphaCoverage: 0,
      alphaColumns: columns,
      alphaRows: rows,
      alphaValues,
      isMonochrome: false
    };
  }

  const contentArea = (maxX - minX + 1) * (maxY - minY + 1);
  minX = Math.max(0, minX - 1);
  minY = Math.max(0, minY - 1);
  maxX = Math.min(columns - 1, maxX + 1);
  maxY = Math.min(rows - 1, maxY + 1);
  return {
    x: minX / columns,
    y: minY / rows,
    width: (maxX - minX + 1) / columns,
    height: (maxY - minY + 1) / rows,
    sourceWidth,
    sourceHeight,
    contentAlphaCoverage: visibleCount / contentArea,
    alphaColumns: columns,
    alphaRows: rows,
    alphaValues,
    isMonochrome: isMonochromeLogoInk(alphaGrid.values, alphaValues)
  };
};

// Straightened ink color. Premultiplied samples hide a grey or black wordmark inside its alpha.
const visibleInkSamples = (luminanceValues, alphaValues) => {
  if (!luminanceValues?.length || luminanceValues.length !== alphaValues?.length) return null;
  const samples = [];
  alphaValues.forEach((alpha, index) => {
    if (alpha <= LOGO_ALPHA_THRESHOLD) return;
    samples.push(Math.min(1, luminanceValues[index] / alpha));
  });
  return samples.length ? samples : null;
};

const inkSpread = samples => {
  const mean = samples.reduce((sum, value) => sum + value, 0) / samples.length;
  const variance = samples.reduce((sum, value) => sum + (value - mean) ** 2, 0) / samples.length;
  return { mean, variance };
};

const isMonochromeLogoInk = (luminanceValues, alphaValues) => {
  const samples = visibleInkSamples(luminanceValues, alphaValues);
  if (!samples) return false;
  return inkSpread(samples).variance < MONOCHROME_LUMINANCE_VARIANCE;
};

export const getCoverImageLogoLayout = (bounds, width, height) => {
  const { x, y, width: contentWidthRatio, height: contentHeightRatio, sourceWidth, sourceHeight } = bounds || {};
  if (!contentWidthRatio || !contentHeightRatio || !sourceWidth || !sourceHeight) return null;

  const contentWidth = contentWidthRatio * sourceWidth;
  const contentHeight = contentHeightRatio * sourceHeight;
  const scale = Math.min(width / contentWidth, height / contentHeight);

  return {
    position: 'absolute',
    width: sourceWidth * scale,
    height: sourceHeight * scale,
    left: (width - contentWidth * scale) / 2 - x * sourceWidth * scale,
    top: (height - contentHeight * scale) / 2 - y * sourceHeight * scale
  };
};

export const getCoverImageLogoLuminance = (luminanceGrid, contentBounds, deviceWidth = 393, frame = null) => {
  const { values, columns, rows, sourceWidth, sourceHeight } = luminanceGrid || {};
  if (!values?.length || !columns || !rows || !sourceWidth || !sourceHeight) return null;

  const cardCoverWidth = frame?.coverWidth ?? (deviceWidth - 24) / 2;
  const cardCoverHeight = frame?.coverHeight ?? cardCoverWidth / 0.85;
  const coverScale = Math.max(cardCoverWidth / sourceWidth, cardCoverHeight / sourceHeight);
  const imageScale = frame ? frame.imageScale ?? coverScale : coverScale * DISCOUNT_CODE_CARD_IMAGE_SCALE;
  const drawnWidth = sourceWidth * imageScale;
  const drawnHeight = sourceHeight * imageScale;
  const offsetX = (cardCoverWidth - drawnWidth) / 2;
  const offsetY = (cardCoverHeight - drawnHeight) / 2;
  const logoWidth = frame?.logoWidth ?? COVER_IMAGE_LOGO_WIDTH;
  const logoHeight = frame?.logoHeight ?? COVER_IMAGE_LOGO_HEIGHT;
  const logoX = frame?.logoX ?? (cardCoverWidth - logoWidth) / 2;
  const logoY = frame?.logoY ?? (cardCoverHeight - logoHeight) / 2;
  const overlayMultiplier = frame?.overlayMultiplier ?? CARD_OVERLAY_MULTIPLIER;
  const logoLayout = getCoverImageLogoLayout(contentBounds, logoWidth, logoHeight) || {
    left: 0,
    top: 0,
    width: logoWidth,
    height: logoHeight
  };
  const samples = [];

  const sampleAt = (x, y) => {
    const sourceX = Math.max(0, Math.min(1, (x - offsetX) / drawnWidth));
    const sourceY = Math.max(0, Math.min(1, (y - offsetY) / drawnHeight));
    const gridX = Math.round(sourceX * (columns - 1));
    const gridY = Math.round(sourceY * (rows - 1));
    const luminance = values[gridY * columns + gridX];
    if (typeof luminance === 'number') samples.push(luminance * overlayMultiplier);
  };

  if (contentBounds?.alphaValues?.length) {
    for (let row = 0; row < contentBounds.alphaRows; row++) {
      for (let column = 0; column < contentBounds.alphaColumns; column++) {
        if (contentBounds.alphaValues[row * contentBounds.alphaColumns + column] <= LOGO_ALPHA_THRESHOLD) continue;
        sampleAt(
          logoX + logoLayout.left + ((column + 0.5) / contentBounds.alphaColumns) * logoLayout.width,
          logoY + logoLayout.top + ((row + 0.5) / contentBounds.alphaRows) * logoLayout.height
        );
      }
    }
  } else {
    for (let row = 0; row < 4; row++) {
      for (let column = 0; column < 8; column++) {
        sampleAt(
          logoX + ((column + 0.5) / 8) * COVER_IMAGE_LOGO_WIDTH,
          logoY + ((row + 0.5) / 4) * COVER_IMAGE_LOGO_HEIGHT
        );
      }
    }
  }

  if (!samples.length) return null;
  samples.sort((a, b) => a - b);
  return samples[Math.floor((samples.length - 1) * CONTRAST_PERCENTILE)];
};

export const getCoverImageLogoMaskConfig = contentBounds => {
  if (contentBounds === undefined) return { shouldTint: false, layers: 1 };
  if (contentBounds === null) return { shouldTint: true, layers: 1 };

  const coverage = contentBounds.contentAlphaCoverage ?? contentBounds.alphaCoverage;
  // Transparent marks always tint. A monochrome wordmark tints too, even when its
  // pixels are opaque, so a grey or black logo can turn white on a dark cover.
  const shouldTint =
    contentBounds.isMonochrome === true || (typeof coverage === 'number' && coverage < OPAQUE_COVERAGE_THRESHOLD);
  const visibleAlpha = (contentBounds.alphaValues || []).filter(alpha => alpha > LOGO_ALPHA_THRESHOLD);
  const averageAlpha = visibleAlpha.length
    ? visibleAlpha.reduce((sum, alpha) => sum + alpha, 0) / visibleAlpha.length
    : 1;
  return { shouldTint, layers: averageAlpha < LOW_ALPHA_THRESHOLD ? 3 : 1 };
};


const imageGrids = new Map();
export const loadArtworkGrid = (url, size) => {
  if (!url) return Promise.resolve(null);
  const key = `${url}|${size}`;
  if (imageGrids.has(key)) return imageGrids.get(key);
  const request = new Promise(resolve => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onerror = () => resolve(null);
    image.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = canvas.height = size;
        const context = canvas.getContext('2d');
        context.drawImage(image, 0, 0, size, size);
        const { data } = context.getImageData(0, 0, size, size);
        const values = [], alphaValues = [];
        for (let i = 0; i < data.length; i += 4) {
          const alpha = data[i + 3] / 255;
          alphaValues.push(alpha);
          values.push(((data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114) / 255) * alpha);
        }
        resolve({ columns: size, rows: size, sourceWidth: image.naturalWidth, sourceHeight: image.naturalHeight, values, alphaValues });
      } catch {
        resolve(null);
      }
    };
    image.src = url;
  });
  if (imageGrids.size >= 150) imageGrids.delete(imageGrids.keys().next().value);
  imageGrids.set(key, request);
  return request;
};
